import React, { createContext, useContext, useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * CMS Validation - Required fields that must exist in Firestore
 * If any of these are missing, the app will show an error state
 */
const validateCMSSettings = (data) => {
  const errors = [];

  // Required brand fields
  if (!data.brandName) errors.push('brandName is required');

  // Required navigation - must have at least one nav link
  if (!data.mainNavLinks || !Array.isArray(data.mainNavLinks) || data.mainNavLinks.length === 0) {
    errors.push('mainNavLinks must be a non-empty array');
  }

  // Required header actions - must have action buttons
  if (!data.header?.actions || !Array.isArray(data.header.actions) || data.header.actions.length === 0) {
    errors.push('header.actions must be a non-empty array');
  }

  // Required styling - check essential color/style properties
  if (!data.header?.bgColor) errors.push('header.bgColor is required');
  if (!data.header?.textColor) errors.push('header.textColor is required');
  if (!data.footer?.bgColor) errors.push('footer.bgColor is required');
  if (!data.footer?.textColor) errors.push('footer.textColor is required');

  return errors;
};

/**
 * CMS Error Display Component
 * Shown when CMS is not properly configured
 */
const CMSErrorDisplay = ({ errors, onRetry }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
    <div className="max-w-lg w-full bg-white rounded-xl shadow-lg p-8 text-center">
      <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <AlertTriangle className="w-8 h-8 text-amber-600" />
      </div>
      <h1 className="text-2xl font-bold text-gray-900 mb-4">
        CMS Configuration Required
      </h1>
      <p className="text-gray-600 mb-6">
        The website content has not been configured in the CMS. Please configure the following required fields in Firestore:
      </p>
      <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
        <p className="text-sm font-mono text-gray-700 mb-2">
          Collection: <code className="bg-gray-200 px-1 rounded">cms_global_settings</code>
        </p>
        <p className="text-sm font-mono text-gray-700 mb-4">
          Document: <code className="bg-gray-200 px-1 rounded">brand-settings</code>
        </p>
        <p className="text-sm font-semibold text-red-600 mb-2">Missing fields:</p>
        <ul className="text-sm text-red-600 space-y-1">
          {errors.map((error, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <span className="text-red-400">•</span>
              <span>{error}</span>
            </li>
          ))}
        </ul>
      </div>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        <RefreshCw className="w-4 h-4" />
        Retry
      </button>
    </div>
  </div>
);

const BrandSettingsContext = createContext(null);

export const BrandSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    setValidationErrors([]);
    // The useEffect will re-run and try to load again
    window.location.reload();
  };

  useEffect(() => {
    let unsub = null;

    try {
      unsub = onSnapshot(
        doc(db, 'cms_global_settings', 'brand-settings'),
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data() || {};

            // Validate CMS data - no fallbacks, CMS is the single source of truth
            const errors = validateCMSSettings(data);

            if (errors.length > 0) {
              setValidationErrors(errors);
              setLoading(false);
              return;
            }

            // CMS data is valid - use it directly without any fallbacks
            const settings = {
              brandName: data.brandName,
              brandTagline: data.brandTagline,
              logoUrl: data.logoUrl,
              faviconUrl: data.faviconUrl,
              primaryColor: data.primaryColor,
              secondaryColor: data.secondaryColor,
              fontFamily: data.fontFamily,
              contact: data.contact || {},
              typography: data.typography || {},
              topbar: data.topbar || {},
              header: data.header || {},
              footer: data.footer || {},
              buttonStyles: data.buttonStyles || {},
              mainNavLinks: data.mainNavLinks || [],
              footerLinks: data.footerLinks || []
            };

            setSettings(settings);
            setValidationErrors([]);
            applyTheme(settings);
          } else {
            // No CMS document exists - show error
            setValidationErrors(['CMS brand-settings document does not exist. Please create it in Firestore.']);
          }
          setLoading(false);
        },
        (error) => {
          console.error('Brand settings load error:', error);
          setError(`Failed to load CMS settings: ${error.message}`);
          setLoading(false);
        }
      );
    } catch (error) {
      console.error('Brand settings initialization error:', error);
      setError(`Failed to initialize CMS: ${error.message}`);
      setLoading(false);
    }

    return () => {
      if (unsub) unsub();
    };
  }, []);

  const applyTheme = (brand) => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;

    if (brand.primaryColor) {
      root.style.setProperty('--color-primary', brand.primaryColor);
    }
    if (brand.secondaryColor) {
      root.style.setProperty('--color-secondary', brand.secondaryColor);
    }
    if (brand.fontFamily) {
      root.style.setProperty('--font-primary', brand.fontFamily);
    }

    // Apply typography
    if (brand.typography) {
      const { primaryFont, secondaryFont, headingWeight, bodyWeight, baseFontSize, lineHeight, letterSpacing, linkUnderline } = brand.typography;

      const fontsToLoad = new Set();
      if (primaryFont && primaryFont !== 'Inter') fontsToLoad.add(primaryFont);
      if (secondaryFont && secondaryFont !== 'Inter' && secondaryFont !== primaryFont) fontsToLoad.add(secondaryFont);

      fontsToLoad.forEach(fontName => {
        const fontId = `google-font-${fontName.replace(/\s+/g, '-').toLowerCase()}`;
        if (!document.getElementById(fontId)) {
          const link = document.createElement('link');
          link.id = fontId;
          link.rel = 'stylesheet';
          link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@300;400;500;600;700&display=swap`;
          document.head.appendChild(link);
        }
      });

      if (primaryFont) root.style.setProperty('--font-heading', `"${primaryFont}", sans-serif`);
      if (secondaryFont) root.style.setProperty('--font-body', `"${secondaryFont}", sans-serif`);
      if (headingWeight) root.style.setProperty('--font-weight-heading', headingWeight);
      if (bodyWeight) root.style.setProperty('--font-weight-body', bodyWeight);
      if (baseFontSize) root.style.setProperty('--font-size-base', `${baseFontSize}px`);
      if (lineHeight) root.style.setProperty('--line-height-base', lineHeight);
      if (letterSpacing) root.style.setProperty('--letter-spacing-base', `${letterSpacing}em`);
      root.style.setProperty('--link-underline', linkUnderline ? 'underline' : 'none');
    }

    // Apply button styles
    if (brand.buttonStyles) {
      const { primaryBg, primaryText, primaryHoverBg, secondaryBg, secondaryText, secondaryBorder, borderRadius, paddingX, paddingY } = brand.buttonStyles;

      if (primaryBg) root.style.setProperty('--btn-primary-bg', primaryBg);
      if (primaryText) root.style.setProperty('--btn-primary-text', primaryText);
      if (primaryHoverBg) root.style.setProperty('--btn-primary-hover-bg', primaryHoverBg);
      if (secondaryBg) root.style.setProperty('--btn-secondary-bg', secondaryBg);
      if (secondaryText) root.style.setProperty('--btn-secondary-text', secondaryText);
      if (secondaryBorder) root.style.setProperty('--btn-secondary-border', secondaryBorder);
      if (borderRadius) root.style.setProperty('--btn-border-radius', `${borderRadius}px`);
      if (paddingX) root.style.setProperty('--btn-padding-x', `${paddingX}px`);
      if (paddingY) root.style.setProperty('--btn-padding-y', `${paddingY}px`);
    }

    if (brand.faviconUrl) {
      const link = document.querySelector("link[rel='icon']");
      if (link) {
        link.href = brand.faviconUrl;
      }
    }
  };

  // Show loading spinner while CMS loads
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-sm">Loading CMS settings...</p>
        </div>
      </div>
    );
  }

  // Show error state if CMS failed to load
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-lg w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            CMS Connection Error
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={handleRetry}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show validation error state if CMS is not properly configured
  if (validationErrors.length > 0) {
    return <CMSErrorDisplay errors={validationErrors} onRetry={handleRetry} />;
  }

  // Show error if settings are still null after loading
  if (!settings) {
    return (
      <CMSErrorDisplay
        errors={['CMS settings could not be loaded. Please check Firestore configuration.']}
        onRetry={handleRetry}
      />
    );
  }

  const value = {
    ...settings,
    loading
  };

  return (
    <BrandSettingsContext.Provider value={value}>
      {children}
    </BrandSettingsContext.Provider>
  );
};

export const useBrandSettings = () => {
  const context = useContext(BrandSettingsContext);
  if (!context) {
    throw new Error('useBrandSettings must be used within a BrandSettingsProvider');
  }
  return context;
};

export default BrandSettingsContext;
